import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  isReturnsRecoverTrigger,
  TriggerKey,
} from "@redotech/redo-flows-common/triggers";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum MarketingConversationType {
  RECOVER = "RECOVER",
  MARKETING = "MARKETING",
  CONCIERGE = "CONCIERGE",
}

export const MarketingConversationTopic = {
  Recover: "recover",
  Returns: "returns",
  MobileMessage: "mobileMessage",
  Concierge: "concierge",
} as const;
export type MarketingConversationTopic =
  (typeof MarketingConversationTopic)[keyof typeof MarketingConversationTopic];

export const QueueType = { Default: "default", Flagged: "flagged" } as const;
export type QueueType = (typeof QueueType)[keyof typeof QueueType];

/**
 * Send-state filter values for conversation logs. "sent" matches conversations
 * whose initial message has gone out; "queued" matches those still waiting to
 * send it. These are multi-selectable — selecting both yields sent-or-queued,
 * selecting neither applies no send-state constraint.
 */
export const ConversationSentStatusFilter = {
  Sent: "sent",
  Queued: "queued",
} as const;
export type ConversationSentStatusFilter =
  (typeof ConversationSentStatusFilter)[keyof typeof ConversationSentStatusFilter];

export const MarketingConversationPriority = {
  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
} as const;
export type MarketingConversationPriority =
  (typeof MarketingConversationPriority)[keyof typeof MarketingConversationPriority];

// Single source of truth: all statuses with their metadata
const statusDefinitions = {
  ready_to_send_reply_message: {
    priority: MarketingConversationPriority.LEVEL_1,
    queue: QueueType.Default,
  },
  reply_message_flagged: {
    priority: MarketingConversationPriority.LEVEL_1,
    queue: QueueType.Flagged,
  },
  ready_to_send_initial_message: {
    priority: MarketingConversationPriority.LEVEL_2,
    queue: QueueType.Default,
  },
  initial_message_flagged: {
    priority: MarketingConversationPriority.LEVEL_2,
    queue: QueueType.Flagged,
  },
  waiting_for_reply_message_generation: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  waiting_for_customer_reply: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  initial_message_rescheduled: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  reply_message_rescheduled: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  sending_message: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  failed_to_send_message: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  draft_message_skipped: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  empty: { priority: MarketingConversationPriority.LEVEL_3, queue: null },
  conversation_ended: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
  handed_off_to_support: {
    priority: MarketingConversationPriority.LEVEL_3,
    queue: null,
  },
} as const satisfies Record<
  string,
  { priority: MarketingConversationPriority; queue: QueueType | null }
>;

type BaseStatus = keyof typeof statusDefinitions;

export type MarketingConversationStatus = BaseStatus;

// Convert snake_case to PascalCase at type level
type SnakeToPascal<S extends string> = S extends `${infer First}_${infer Rest}`
  ? `${Capitalize<First>}${SnakeToPascal<Rest>}`
  : Capitalize<S>;

type StatusObject = { [K in BaseStatus as SnakeToPascal<K>]: K };

function snakeToPascal(s: string): string {
  return s
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

export const MarketingConversationStatus = Object.fromEntries(
  Object.keys(statusDefinitions).map((k) => [snakeToPascal(k), k]),
) as StatusObject;

/**
 * Statuses where an initial outreach message exists but has not been sent yet —
 * i.e. genuinely "queued" to send. This deliberately excludes terminal/failed
 * states (failed_to_send_message, draft_message_skipped, conversation_ended,
 * empty, etc.) that also lack a lastInitialMessageAt but are NOT waiting to
 * send. Use this rather than `lastInitialMessageAt == null` to identify queued
 * conversations.
 */
export const QUEUED_INITIAL_MESSAGE_STATUSES: MarketingConversationStatus[] = [
  MarketingConversationStatus.ReadyToSendInitialMessage,
  MarketingConversationStatus.InitialMessageFlagged,
  MarketingConversationStatus.InitialMessageRescheduled,
];

export function getPrioritiesToShowInQueue(): MarketingConversationPriority[] {
  return [
    MarketingConversationPriority.LEVEL_1,
    MarketingConversationPriority.LEVEL_2,
  ];
}

export function getMarketingConversationPriority(
  status: MarketingConversationStatus,
): MarketingConversationPriority {
  return statusDefinitions[status].priority;
}

export function getQueueTypeForStatus(
  status: MarketingConversationStatus,
): QueueType | null {
  return statusDefinitions[status].queue;
}

/** Statuses that place a drafted message in the human review queue. */
export function humanReviewQueueStatuses(): readonly MarketingConversationStatus[] {
  return [
    ...getStatuesToShowInQueue(QueueType.Default),
    ...getStatuesToShowInQueue(QueueType.Flagged),
  ];
}

export function getStatuesToShowInQueue(
  queueType: QueueType,
): MarketingConversationStatus[] {
  return Object.entries(statusDefinitions)
    .filter(([, v]) => v.queue === queueType)
    .map(([k]) => k as BaseStatus);
}

export function getTopicForTrigger(
  triggerKey?: TriggerKey,
): MarketingConversationTopic {
  if (triggerKey && isReturnsRecoverTrigger(triggerKey)) {
    return MarketingConversationTopic.Returns;
  }
  return MarketingConversationTopic.Recover;
}

const marketingConversationSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  customerId: zExt.objectId(),
  type: createEnum(MarketingConversationType),
  channel: createEnum(MarketingChannel),
  channelId: z.string(),
  lastMessageAt: z.date().optional(),
  lastInitialMessageAt: z.date().optional(),
  priority: createEnum(MarketingConversationPriority),
  status: createEnum(MarketingConversationStatus).default(
    MarketingConversationStatus.Empty,
  ),
  currentQueueTopic: createEnum(MarketingConversationTopic).default(
    MarketingConversationTopic.Recover,
  ),
  hasInboundMessage: z.boolean().optional(),
  claimedBy: z.string().nullish(),
  claimedAt: z.date().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type MarketingConversation = z.infer<
  typeof marketingConversationSchema_
>;
export const marketingConversationSchema: z.ZodType<MarketingConversation> =
  marketingConversationSchema_;

const marketingConversationHydratedSchema_ =
  marketingConversationSchema_.extend({
    customerName: z.string().optional(),
    teamName: z.string().optional(),
  });

export const marketingConversationHydratedSchema: z.ZodType<MarketingConversationHydrated> =
  marketingConversationHydratedSchema_;

export type MarketingConversationHydrated = z.infer<
  typeof marketingConversationHydratedSchema_
>;

/**
 * Cursor for queue pagination.
 * Sort order is: priority ASC, lastMessageAt ASC, _id ASC (FIFO)
 */
const queueCursorSchema_ = z.object({
  priority: createEnum(MarketingConversationPriority),
  lastMessageAt: z.coerce.date().nullable(),
  _id: zExt.objectId(),
});
export const queueCursorSchema: z.ZodType<QueueCursor> = queueCursorSchema_;
export interface QueueCursor extends z.infer<typeof queueCursorSchema_> {}
